import React from "react";
import { useMyStore } from "store";
import styles from "./MobileDownloadModal.module.scss";
import ModalBackground from "assets/img-winaz/download-popup/bgpopup2.webp";
import CloseButtonImage from "assets/img-winaz/button/btnclose.webp";
import GooglePlayButtonImage from "assets/img-winaz/download-popup/btngoogleplay.webp";
import APKButtonImage from "assets/img-winaz/download-popup/BtnAPK.webp";
import RoiBestButtonImage from "assets/img-winaz/download-popup/btnROIBEST.webp";
import AppStoreButtonImage from "assets/img-winaz/download-popup/btnappstore.webp";
import IPAButtonImage from "assets/img-winaz/download-popup/BTNIPA.webp";

type Platform = "android" | "ios";

type MobileDownloadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  platform: Platform;
};

const APP_STORE_2_URL =
  "https://apps.apple.com/us/app/gizzada-forge/id6761264635";

const MobileDownloadModal: React.FunctionComponent<
  MobileDownloadModalProps
> = ({ isOpen, onClose, platform }) => {
  const bootstrap = useMyStore((s) => s.bootstrap);

  const handleOverlayClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const openInNewTab = React.useCallback((url: string) => {
    if (typeof window === "undefined") {
      return;
    }
    if (url === "#") {
      console.warn("Download URL not configured yet");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const handleGooglePlayDownload = React.useCallback(() => {
    openInNewTab(bootstrap?.androidApp?.googleStoreUrl || "");
  }, [openInNewTab, bootstrap?.androidApp?.googleStoreUrl]);

  const handleAPKDirectDownload = React.useCallback(() => {
    openInNewTab(bootstrap?.androidApp?.apkUrl || "");
  }, [openInNewTab, bootstrap?.androidApp?.apkUrl]);

  const handleRoiBestDownload = React.useCallback(() => {
    openInNewTab(bootstrap?.androidApp?.roibestUrl || "");
  }, [openInNewTab, bootstrap?.androidApp?.roibestUrl]);

  const handleAppStoreDownload = React.useCallback(() => {
    openInNewTab(bootstrap?.iosApp?.appStoreUrl || "");
  }, [openInNewTab, bootstrap?.iosApp?.appStoreUrl]);

  const handleAppStore2Download = React.useCallback(() => {
    openInNewTab(APP_STORE_2_URL);
  }, [openInNewTab]);

  const handleIPADirectDownload = React.useCallback(() => {
    openInNewTab(bootstrap?.iosApp?.ipaUrl || "");
  }, [openInNewTab, bootstrap?.iosApp?.ipaUrl]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const isAndroid = platform === "android";
  const title = isAndroid ? "TẢI APP ANDROID" : "TẢI APP IOS";

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modalWrapper}>
        <div
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          style={{ backgroundImage: `url(${ModalBackground})` }}
        >
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Đóng"
          >
            <img src={CloseButtonImage} alt="" aria-hidden="true" />
          </button>

          <h2 className={styles.title}>{title}</h2>

          <div
            className={`${styles.buttonsLayer} ${!isAndroid ? styles.buttonsLayerIOS : ""}`}
          >
            {isAndroid ? (
              <>
                {/* Android - APK first (recommended) */}
                {bootstrap?.androidApp?.apkUrl && (
                  <div
                    className={`${styles.buttonGroup} ${styles.recommended}`}
                  >
                    <p
                      className={`${styles.buttonLabel} ${styles.recommendedLabel}`}
                    >
                      Bản cài trực tiếp APK
                      <span className={styles.recommendedBadge}>
                        Khuyên dùng
                      </span>
                    </p>
                    <button
                      type="button"
                      className={styles.downloadButton}
                      onClick={handleAPKDirectDownload}
                    >
                      <img src={APKButtonImage} alt="Bản cài .APK" />
                    </button>
                  </div>
                )}

                {bootstrap?.androidApp?.googleStoreUrl && (
                  <div className={styles.buttonGroup}>
                    <p className={styles.buttonLabel}>Bản cài Googleplay</p>
                    <button
                      type="button"
                      className={styles.downloadButton}
                      onClick={handleGooglePlayDownload}
                    >
                      <img
                        src={GooglePlayButtonImage}
                        alt="GET IT ON Google Play"
                      />
                    </button>
                  </div>
                )}

                {bootstrap?.androidApp?.roibestUrl && (
                  <div
                    className={`${styles.buttonGroup} ${styles.recommended}`}
                  >
                    <p className={styles.buttonLabel}>Bản cài Backup</p>
                    <button
                      type="button"
                      className={styles.downloadButton}
                      onClick={handleRoiBestDownload}
                    >
                      <img src={RoiBestButtonImage} alt="Backup" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* iOS - AppStore first (recommended) */}
                {bootstrap?.iosApp?.appStoreUrl && (
                  <div
                    className={`${styles.buttonGroup} ${styles.recommended}`}
                  >
                    <p
                      className={`${styles.buttonLabel} ${styles.recommendedLabel}`}
                    >
                      Bản cài AppStore
                      <span className={styles.recommendedBadge}>
                        Khuyên dùng
                      </span>
                    </p>
                    <button
                      type="button"
                      className={styles.downloadButton}
                      onClick={handleAppStoreDownload}
                    >
                      <img
                        src={AppStoreButtonImage}
                        alt="Download on the App Store"
                      />
                    </button>
                  </div>
                )}

                {/* <div className={`${styles.buttonGroup} ${styles.recommended}`}>
                  <p
                    className={`${styles.buttonLabel} ${styles.recommendedLabel}`}
                  >
                    Bản cài AppStore 2
                    <span className={styles.recommendedBadge}>Khuyên dùng</span>
                  </p>
                  <button
                    type="button"
                    className={styles.downloadButton}
                    onClick={handleAppStore2Download}
                  >
                    <img
                      src={AppStoreButtonImage}
                      alt="Download on the App Store 2"
                    />
                  </button>
                </div> */}

                {bootstrap?.iosApp?.ipaUrl && (
                  <div className={styles.buttonGroup}>
                    <p className={styles.buttonLabel}>Bản cài trực tiếp IPA</p>
                    <button
                      type="button"
                      className={styles.downloadButton}
                      onClick={handleIPADirectDownload}
                    >
                      <img src={IPAButtonImage} alt="Bản cài .IPA" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileDownloadModal;
